// src/services/realBlockchainData.ts

/**
 * Real Blockchain Data Fetcher for SPX6900 Whale Tracking
 * This service fetches actual transaction data from blockchain explorers
 * and RPC endpoints to provide accurate whale activity tracking.
 */

import axios from 'axios';
import { Connection, PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress, getAccount } from '@solana/spl-token';
import { SPX6900_TOKEN_CONTRACTS } from '../constants';
import { BlockchainNetwork } from '../types';
import { getSPX6900PriceForCalculations } from './spx6900Price';
import { getCachedData, setCachedData, getStaleData } from './cache';

// ========== Caching Configuration ==========

const BLOCKCHAIN_CACHE_CONFIG = {
  // Cache TTLs for different data types
  BALANCE_TTL: 10 * 60, // 10 minutes for balances
  TRANSFERS_TTL: 30 * 60, // 30 minutes for transfers
  ACTIVITY_TTL: 15 * 60, // 15 minutes for activity data
  
  // Rate limiting
  MIN_REQUEST_INTERVAL: 2000, // 2 seconds between requests to same API
};

// Rate limiting tracking
const apiRequestTimestamps = new Map<string, number>();

/**
 * Check if we can make an API request (rate limiting)
 */
const canMakeRequest = (apiKey: string): boolean => {
  const now = Date.now();
  const lastRequest = apiRequestTimestamps.get(apiKey) || 0;
  return (now - lastRequest) >= BLOCKCHAIN_CACHE_CONFIG.MIN_REQUEST_INTERVAL;
};

/**
 * Record an API request timestamp
 */
const recordRequest = (apiKey: string): void => {
  apiRequestTimestamps.set(apiKey, Date.now());
};

// ========== Types ==========

interface TokenTransfer {
  timestamp: number;
  signature: string;
  from: string;
  to: string;
  amount: number;
  direction: 'incoming' | 'outgoing';
  isSwap: boolean;
  transactionType: 'buy' | 'sell' | 'transfer';
}

interface SPX6900Activity {
  address: string;
  network: BlockchainNetwork;
  currentBalance: number | null; // null indicates API failure
  currentBalanceUSD: number | null; // null indicates API failure
  totalBought: number;
  totalSold: number;
  totalBoughtUSD: number;
  totalSoldUSD: number;
  firstActivity: number;
  lastActivity: number;
  isCurrentlySelling: boolean;
  sellPressure: 'none' | 'low' | 'medium' | 'high';
  recentTransfers: TokenTransfer[];
  validationUrl: string;
  confidence: 'high' | 'medium' | 'low';
  errors: string[];
}

interface ValidationResult {
  isValid: boolean;
  discrepancies: string[];
  explorerBalance?: number;
  ourBalance?: number;
}

// ========== Ethereum Implementation ==========

class EthereumDataFetcher {
  // Updated to Etherscan V2 API
  private readonly ETHERSCAN_API_V2 = 'https://api.etherscan.io/v2/api';
  private readonly ETHEREUM_CHAIN_ID = 1; // Ethereum mainnet
  private readonly ALCHEMY_RPC = 'https://eth-mainnet.g.alchemy.com/v2/demo';
  private readonly INFURA_RPC = 'https://mainnet.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161';
  
  /**
   * Fetch real SPX6900 activity for an Ethereum address
   */
  async fetchSPX6900Activity(address: string): Promise<SPX6900Activity> {
    const errors: string[] = [];
    let confidence: 'high' | 'medium' | 'low' = 'high';

    try {
      console.log(`🔍 Fetching real SPX6900 data for Ethereum address: ${address}`);
      
      // Get current balance using our API endpoint
      const currentBalance = await this.getCurrentSPX6900Balance(address);
      console.log(`💰 Current SPX6900 balance: ${currentBalance}`);
      
      // Get SPX6900 token transfers
      const transfers = await this.getSPX6900Transfers(address);
      console.log(`📜 Found ${transfers.length} transfers`);
      
      // Analyze transfers
      const analysis = this.analyzeBuySellActivity(transfers, address);
      
      // Get current SPX6900 price
      const spx6900Price = await this.getSPX6900Price();
      
      // Determine selling pressure and current selling status
      const sellPressure = this.calculateSellPressure(transfers);
      const isCurrentlySelling = this.isCurrentlySelling(transfers);
      
      const activity: SPX6900Activity = {
        address,
        network: 'Ethereum',
        currentBalance,
        currentBalanceUSD: currentBalance !== null ? currentBalance * spx6900Price : null,
        totalBought: analysis.totalBought,
        totalSold: analysis.totalSold,
        totalBoughtUSD: analysis.totalBought * spx6900Price,
        totalSoldUSD: analysis.totalSold * spx6900Price,
        firstActivity: analysis.firstActivity,
        lastActivity: analysis.lastActivity,
        isCurrentlySelling,
        sellPressure,
        recentTransfers: transfers.slice(0, 10),
        validationUrl: `https://etherscan.io/address/${address}`,
        confidence,
        errors
      };

      console.log(`✅ SPX6900 analysis complete for Ethereum ${address}:`);
      console.log(`   Current Balance: ${currentBalance !== null ? currentBalance.toFixed(2) + ' SPX6900' : 'Error - API Failed'}`);
      console.log(`   Total Bought: ${analysis.totalBought.toFixed(2)} SPX6900`);
      console.log(`   Total Sold: ${analysis.totalSold.toFixed(2)} SPX6900`);
      console.log(`   Currently Selling: ${isCurrentlySelling}`);
      console.log(`   Sell Pressure: ${sellPressure}`);

      return activity;

    } catch (error) {
      console.error(`❌ Error fetching Ethereum SPX6900 data for ${address}:`, error);
      errors.push(`Failed to fetch data: ${error}`);
      
      return {
        address,
        network: 'Ethereum',
        currentBalance: null, // null indicates API failure
        currentBalanceUSD: null, // null indicates API failure
        totalBought: 0,
        totalSold: 0,
        totalBoughtUSD: 0,
        totalSoldUSD: 0,
        firstActivity: 0,
        lastActivity: 0,
        isCurrentlySelling: false,
        sellPressure: 'none',
        recentTransfers: [],
        validationUrl: `https://etherscan.io/address/${address}`,
        confidence: 'low',
        errors
      };
    }
  }

  /**
   * Get current SPX6900 balance for Ethereum address (with caching)
   */
  public async getCurrentSPX6900Balance(address: string): Promise<number | null> {
    const cacheKey = `eth_balance:${address}`;

    // Force refresh for addresses that previously had incorrect cached values
    const forceRefreshAddresses = [
      '0x85dead62a85cff13fdbe8dc1005f4593f578598b'
    ];
    const shouldForceRefresh = forceRefreshAddresses.some(
      a => a.toLowerCase() === address.toLowerCase()
    );

    try {
      // Try cache first (unless force refresh)
      if (!shouldForceRefresh) {
        const cachedBalance = await getCachedData<number>(cacheKey);
        if (cachedBalance !== null && cachedBalance !== undefined && cachedBalance > 0) {
          console.log(`📦 Using cached Ethereum balance for ${address}: ${cachedBalance}`);
          return cachedBalance;
        }
      } else {
        console.log(`🔄 Force refreshing Ethereum balance for ${address} (bypassing cache)`);
      }

      // Check rate limiting
      if (!canMakeRequest('etherscan')) {
        console.log(`⏳ Rate limited - trying stale data for ${address}`);
        const staleBalance = await getStaleData<number>(cacheKey);
        if (staleBalance !== null && staleBalance !== undefined && staleBalance > 0) {
          console.log(`🔄 Using stale Ethereum balance for ${address}: ${staleBalance}`);
          return staleBalance;
        }
      }
      
      const spx6900ContractAddress = SPX6900_TOKEN_CONTRACTS.ETHEREUM.address;
      console.log(`💰 Getting REAL SPX6900 balance for Ethereum ${address}`);
      console.log(`🎯 SPX6900 contract address: ${spx6900ContractAddress}`);
      
      // Try real API methods for actual blockchain data
      console.log(`💰 Fetching real Ethereum SPX6900 balance from APIs`);
      
      // Check if we have Etherscan API key for real data
      const etherscanApiKey = process.env.REACT_APP_ETHERSCAN_API_KEY;
      console.log(`🔑 Etherscan API key status: ${etherscanApiKey ? 'Present' : 'Missing'}`);
      
      if (etherscanApiKey) {
        // Retry logic with exponential backoff
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            // Add delay between retries to avoid rate limiting
            if (attempt > 1) {
              const delay = Math.pow(2, attempt - 1) * 1000; // 1s, 2s, 4s
              console.log(`⏳ Retry ${attempt}/${maxRetries} after ${delay}ms delay...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }

            // Record API request for rate limiting
            recordRequest('etherscan');
            console.log(`💰 Trying Etherscan V2 token balance API (attempt ${attempt})...`);

            // Use Etherscan V2 API with chain ID for Ethereum mainnet
            const response = await axios.get(this.ETHERSCAN_API_V2, {
              params: {
                chainid: this.ETHEREUM_CHAIN_ID,
                module: 'account',
                action: 'tokenbalance',
                contractaddress: spx6900ContractAddress,
                address: address,
                tag: 'latest',
                apikey: etherscanApiKey
              },
              timeout: 15000
            });

            console.log(`💰 Etherscan response:`, response.data);

            if (response.data.status === '1') {
              const rawBalance = response.data.result;
              const balance = parseFloat(rawBalance) / Math.pow(10, SPX6900_TOKEN_CONTRACTS.ETHEREUM.decimals);

              console.log(`💰 Raw balance from Etherscan: ${rawBalance}`);
              console.log(`💰 Formatted balance: ${balance} SPX6900`);
              console.log(`💰 Balance in millions: ${(balance / 1000000).toFixed(2)}M SPX6900`);
              console.log(`💰 Address: ${address}`);

              // Cache the successful result
              await setCachedData(cacheKey, balance, BLOCKCHAIN_CACHE_CONFIG.BALANCE_TTL);
              console.log(`💰 ✅ Found and cached SPX6900 balance via Etherscan: ${balance}`);
              return balance;
            } else {
              // Check if it's a rate limit error
              const errorMsg = response.data.message || response.data.result || '';
              if (errorMsg.includes('rate limit') || errorMsg === 'NOTOK') {
                console.warn(`⚠️ Etherscan rate limited (attempt ${attempt}):`, errorMsg);
                if (attempt < maxRetries) continue; // Retry
              }
              console.warn(`⚠️ Etherscan returned error:`, response.data);
            }
          } catch (error: any) {
            console.warn(`⚠️ Etherscan API failed (attempt ${attempt}):`, error.message);

            // Check if it's a rate limit error
            if (error.response?.data?.message?.includes('rate limit')) {
              console.warn(`🚫 Etherscan rate limit detected`);
              if (attempt < maxRetries) continue; // Retry

              const staleBalance = await getStaleData<number>(cacheKey);
              if (staleBalance !== null && staleBalance !== undefined && staleBalance > 0) {
                console.log(`🔄 Using stale balance due to rate limit: ${staleBalance}`);
                return staleBalance as number;
              }
            }
            if (attempt < maxRetries) continue; // Retry on other errors too
          }
        }
      } else {
        console.error(`❌ NO ETHERSCAN API KEY - Cannot fetch real Ethereum SPX6900 balances`);
        console.error(`❌ Set REACT_APP_ETHERSCAN_API_KEY environment variable to fetch real data`);
        console.error(`❌ Without API key, all wallets will show 0 SPX6900 balance`);
      }
      
      console.error(`❌ FAILED TO GET REAL ETHEREUM BALANCE - All methods failed for ${address}`);
      
      // REMOVED: Hardcoded Ethereum fallback balances - need to fix the actual API issues
      
      console.log(`📊 Attempting to calculate balance from transfer history as fallback...`);
      
      try {
        // Fallback: Calculate balance from transfer history
        const transfers = await this.getSPX6900Transfers(address);
        const calculatedBalance = this.calculateCurrentBalanceFromTransfers(transfers, address);
        
        if (calculatedBalance > 0) {
          console.log(`📊 ✅ Using calculated balance as fallback: ${calculatedBalance} SPX6900`);
          // Cache the calculated balance for a shorter time since it's not real-time
          await setCachedData(cacheKey, calculatedBalance, 300); // 5 minutes cache
          return calculatedBalance;
        } else {
          console.log(`📊 ❌ Calculated balance is also 0, likely wallet has no SPX6900 tokens`);
        }
      } catch (error) {
        console.error(`❌ Failed to calculate balance from transfers:`, error);
      }
      
      // CRITICAL: If all Ethereum methods failed, return error state
      console.error(`🚨 CRITICAL: All Ethereum balance fetching methods failed for ${address}`);
      console.error(`🚨 Returning error state for Ethereum wallet`);
      
      return null; // Indicates API failure
      
    } catch (error) {
      console.error(`❌ Critical error getting Ethereum SPX6900 balance:`, error);
      console.error(`🚨 This is likely an API infrastructure issue, not a zero balance`);
      return null; // Indicates API failure
    }
  }

  /**
   * Get SPX6900 token transfers for Ethereum address (with caching)
   */
  private async getSPX6900Transfers(address: string): Promise<TokenTransfer[]> {
    const cacheKey = `eth_transfers:${address}`;
    
    try {
      // Try cache first
      const cachedTransfers = await getCachedData<TokenTransfer[]>(cacheKey);
      if (cachedTransfers && cachedTransfers.length > 0) {
        console.log(`📦 Using cached Ethereum transfers for ${address}: ${cachedTransfers.length} transfers`);
        return cachedTransfers;
      }
      
      // Check rate limiting
      if (!canMakeRequest('etherscan-transfers')) {
        console.log(`⏳ Rate limited - trying stale transfer data for ${address}`);
        const staleTransfers = await getStaleData<TokenTransfer[]>(cacheKey);
        if (staleTransfers && staleTransfers.length > 0) {
          console.log(`🔄 Using stale Ethereum transfers for ${address}: ${staleTransfers.length} transfers`);
          return staleTransfers;
        }
      }
      
      const spx6900Contract = SPX6900_TOKEN_CONTRACTS.ETHEREUM.address;
      console.log(`🔍 Fetching SPX6900 transfers for Ethereum ${address}`);

      // Use Etherscan V2 API with chain ID for Ethereum mainnet
      console.log(`📡 Fetching real SPX6900 transfers via Etherscan V2 for Ethereum ${address}`);
      // Record API request for rate limiting
      recordRequest('etherscan-transfers');

      const response = await axios.get(this.ETHERSCAN_API_V2, {
        params: {
          chainid: this.ETHEREUM_CHAIN_ID,
          module: 'account',
          action: 'tokentx',
          contractaddress: spx6900Contract,
          address: address,
          startblock: 0,
          endblock: 99999999,
          sort: 'desc',
          apikey: process.env.REACT_APP_ETHERSCAN_API_KEY
        },
        timeout: 15000
      });

      console.log(`📡 Etherscan transfers response:`, response.data);

      if (response.data.status !== '1' || !response.data.result) {
        console.warn(`❌ No SPX6900 transfers found for ${address}`);
        // Return empty array if no transfers found
        await setCachedData(cacheKey, [], 60); // 1 minute for failures
        return [];
      }

      // Convert to our TokenTransfer format
      const transfers: TokenTransfer[] = response.data.result.map((tx: any) => {
        const isOutgoing = tx.from.toLowerCase() === address.toLowerCase();
        const amount = parseFloat(tx.value) / Math.pow(10, parseInt(tx.tokenDecimal));
        
        return {
          timestamp: parseInt(tx.timeStamp) * 1000,
          signature: tx.hash,
          from: tx.from,
          to: tx.to,
          amount,
          direction: isOutgoing ? 'outgoing' : 'incoming',
          isSwap: false, // Would need more analysis
          transactionType: isOutgoing ? 'sell' : 'buy'
        };
      });

      const sortedTransfers = transfers.sort((a, b) => b.timestamp - a.timestamp);
      
      // Cache the successful result
      await setCachedData(cacheKey, sortedTransfers, BLOCKCHAIN_CACHE_CONFIG.TRANSFERS_TTL);
      console.log(`📊 Found and cached ${transfers.length} SPX6900 transfers for Ethereum ${address}`);
      return sortedTransfers;

    } catch (error: any) {
      console.error(`Error fetching Ethereum SPX6900 transfers:`, error);
      
      // Check if it's a rate limit error
      if (error.response?.data?.message?.includes('rate limit')) {
        console.warn(`🚫 Etherscan transfer rate limit detected, using stale data`);
        const staleTransfers = await getStaleData<TokenTransfer[]>(cacheKey);
        if (staleTransfers && staleTransfers.length > 0) {
          console.log(`🔄 Using stale transfers due to rate limit: ${staleTransfers.length} transfers`);
          return staleTransfers;
        }
      }
      
      // Return empty array on error
      await setCachedData(cacheKey, [], 60); // 1 minute for error fallback
      return [];
    }
  }

  // Mock transfer generation functions removed - using only real blockchain data

  // Reuse utility methods from Solana implementation
  private analyzeBuySellActivity(transfers: TokenTransfer[], address: string) {
    let totalBought = 0;
    let totalSold = 0;
    let firstActivity = Date.now();
    let lastActivity = 0;

    for (const transfer of transfers) {
      if (transfer.timestamp < firstActivity) {
        firstActivity = transfer.timestamp;
      }
      if (transfer.timestamp > lastActivity) {
        lastActivity = transfer.timestamp;
      }

      if (transfer.direction === 'incoming') {
        totalBought += transfer.amount;
      } else if (transfer.direction === 'outgoing') {
        totalSold += transfer.amount;
      }
    }

    return { totalBought, totalSold, firstActivity, lastActivity };
  }

  private isCurrentlySelling(transfers: TokenTransfer[]): boolean {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentSells = transfers.filter(t => 
      t.timestamp > sevenDaysAgo && t.direction === 'outgoing'
    );
    return recentSells.length > 0;
  }

  private calculateSellPressure(transfers: TokenTransfer[]): 'none' | 'low' | 'medium' | 'high' {
    const last7Days = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const last24Hours = Date.now() - (24 * 60 * 60 * 1000);
    
    const recentSells = transfers.filter(t => 
      t.timestamp > last7Days && t.direction === 'outgoing'
    );
    
    const dailySells = transfers.filter(t => 
      t.timestamp > last24Hours && t.direction === 'outgoing'
    );

    if (dailySells.length >= 3) return 'high';
    if (recentSells.length >= 5) return 'high';
    if (recentSells.length >= 3) return 'medium';
    if (recentSells.length >= 1) return 'low';
    return 'none';
  }

  /**
   * Calculate current balance from transfer history
   */
  private calculateCurrentBalanceFromTransfers(transfers: TokenTransfer[], address: string): number {
    let balance = 0;

    for (const transfer of transfers) {
      if (transfer.direction === 'incoming') {
        balance += transfer.amount;
      } else if (transfer.direction === 'outgoing') {
        balance -= transfer.amount;
      }
    }

    return Math.max(0, balance); // Can't have negative balance
  }

  /**
   * Get current SPX6900 price using centralized service
   */
  private async getSPX6900Price(): Promise<number> {
    return await getSPX6900PriceForCalculations();
  }
}

// ========== Base Implementation ==========

class BaseDataFetcher {
  private readonly BASESCAN_API_BASE = 'https://api.basescan.org/api';
  
  /**
   * Fetch real SPX6900 activity for a Base address
   */
  async fetchSPX6900Activity(address: string): Promise<SPX6900Activity> {
    const errors: string[] = [];
    let confidence: 'high' | 'medium' | 'low' = 'high';

    try {
      console.log(`🔍 Fetching real SPX6900 data for Base address: ${address}`);
      
      const currentBalance = await this.getCurrentSPX6900Balance(address);
      const transfers = await this.getSPX6900Transfers(address);
      const analysis = this.analyzeBuySellActivity(transfers, address);
      const spx6900Price = await this.getSPX6900Price();
      const sellPressure = this.calculateSellPressure(transfers);
      const isCurrentlySelling = this.isCurrentlySelling(transfers);
      
      const activity: SPX6900Activity = {
        address,
        network: 'Base',
        currentBalance,
        currentBalanceUSD: currentBalance !== null ? currentBalance * spx6900Price : null,
        totalBought: analysis.totalBought,
        totalSold: analysis.totalSold,
        totalBoughtUSD: analysis.totalBought * spx6900Price,
        totalSoldUSD: analysis.totalSold * spx6900Price,
        firstActivity: analysis.firstActivity,
        lastActivity: analysis.lastActivity,
        isCurrentlySelling,
        sellPressure,
        recentTransfers: transfers.slice(0, 10),
        validationUrl: `https://basescan.org/address/${address}`,
        confidence,
        errors
      };

      console.log(`✅ SPX6900 analysis complete for Base ${address}:`);
      console.log(`   Current Balance: ${currentBalance !== null ? currentBalance.toFixed(2) + ' SPX6900' : 'Error - API Failed'}`);
      console.log(`   Currently Selling: ${isCurrentlySelling}`);

      return activity;

    } catch (error) {
      console.error(`❌ Error fetching Base SPX6900 data for ${address}:`, error);
      errors.push(`Failed to fetch data: ${error}`);
      
      return {
        address,
        network: 'Base',
        currentBalance: null, // null indicates API failure
        currentBalanceUSD: null, // null indicates API failure
        totalBought: 0,
        totalSold: 0,
        totalBoughtUSD: 0,
        totalSoldUSD: 0,
        firstActivity: 0,
        lastActivity: 0,
        isCurrentlySelling: false,
        sellPressure: 'none',
        recentTransfers: [],
        validationUrl: `https://basescan.org/address/${address}`,
        confidence: 'low',
        errors
      };
    }
  }

  public async getCurrentSPX6900Balance(address: string): Promise<number | null> {
    const cacheKey = `base_balance:${address}`;

    // Force refresh for addresses that previously had incorrect cached values
    const forceRefreshAddresses = [
      '0x444C5D6994b96Fb9acb9Fa71017cBE7c54c68551',
      '0xe0f693f4dc486fbe734e3afff5fe03fba08a64f3',
      '0x7C005131eDCE9c25CfB2532DBf5869F644eAaD7F'
    ];
    const shouldForceRefresh = forceRefreshAddresses.some(
      a => a.toLowerCase() === address.toLowerCase()
    );

    try {
      // Try cache first (unless force refresh)
      if (!shouldForceRefresh) {
        const cachedBalance = await getCachedData<number>(cacheKey);
        if (cachedBalance !== null && cachedBalance !== undefined && cachedBalance > 0) {
          console.log(`📦 Using cached Base balance for ${address}: ${cachedBalance}`);
          return cachedBalance;
        }
      } else {
        console.log(`🔄 Force refreshing Base balance for ${address} (bypassing cache)`);
      }

      // Check rate limiting - wait if needed
      if (!canMakeRequest('etherscan-base')) {
        console.log(`⏳ Rate limited for Base - waiting before request...`);
        await new Promise(resolve => setTimeout(resolve, 2500)); // Wait 2.5 seconds
      }

      const spx6900ContractAddress = SPX6900_TOKEN_CONTRACTS.BASE.address;

      console.log(`💰 Getting REAL SPX6900 balance for Base ${address}`);

      // Use Etherscan V2 API (supports Base chain)
      const etherscanApiKey = process.env.REACT_APP_ETHERSCAN_API_KEY || 'N132RBDAMUJXFJ2Z6N8UKUP36BJMNI7PW9';
      console.log(`🔑 Etherscan V2 API key configured for Base chain`);

      if (etherscanApiKey) {
        // Retry logic with exponential backoff
        const maxRetries = 3;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            // Add delay between retries to avoid rate limiting
            if (attempt > 1) {
              const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s (increased delays)
              console.log(`⏳ Retry ${attempt}/${maxRetries} after ${delay}ms delay...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }

            // Record request for rate limiting
            recordRequest('etherscan-base');

            console.log(`📡 Calling Etherscan V2 API for Base wallet ${address} (attempt ${attempt})`);
            console.log(`   Contract: ${spx6900ContractAddress}`);

            // Use Etherscan V2 API with Base chain ID
            const response = await axios.get('https://api.etherscan.io/v2/api', {
              params: {
                chainid: 8453, // Base mainnet chain ID
                module: 'account',
                action: 'tokenbalance',
                contractaddress: spx6900ContractAddress,
                address: address,
                tag: 'latest',
                apikey: etherscanApiKey
              },
              timeout: 15000
            });

            console.log(`📡 Etherscan V2 response:`, response.data);

            if (response.data.status === '1') {
              const rawBalance = response.data.result;
              const balance = parseFloat(rawBalance) / Math.pow(10, SPX6900_TOKEN_CONTRACTS.BASE.decimals);
              console.log(`💰 ✅ Found SPX6900 balance via Etherscan V2:`);
              console.log(`   Raw: ${rawBalance}`);
              console.log(`   Formatted: ${balance.toLocaleString()} SPX6900`);

              // Cache the successful result
              if (balance > 0) {
                await setCachedData(cacheKey, balance, BLOCKCHAIN_CACHE_CONFIG.BALANCE_TTL);
                console.log(`📦 Cached Base balance for ${address}: ${balance}`);
              }
              return balance;
            } else {
              // Check if it's a rate limit error
              const errorMsg = response.data.message || response.data.result || '';
              if (errorMsg.includes('rate limit') || errorMsg === 'NOTOK') {
                console.warn(`⚠️ Etherscan V2 rate limited (attempt ${attempt}):`, errorMsg);
                if (attempt < maxRetries) continue; // Retry
              }
              console.warn(`⚠️ Etherscan V2 returned status 0:`, errorMsg);
            }
          } catch (error: any) {
            console.error(`⚠️ Etherscan V2 API failed (attempt ${attempt}):`, error.message);
            if (attempt < maxRetries) continue; // Retry
          }
        }
      }
      
      console.error(`❌ FAILED TO GET REAL BASE BALANCE for ${address}`);
      
      console.log(`📊 Attempting to calculate Base balance from transfer history as fallback...`);
      
      try {
        // Fallback: Calculate balance from transfer history
        const transfers = await this.getSPX6900Transfers(address);
        const calculatedBalance = this.calculateCurrentBalanceFromTransfers(transfers, address);
        const fallbackCacheKey = `base_balance_calculated:${address}`;
        
        if (calculatedBalance > 0) {
          console.log(`📊 ✅ Using calculated Base balance as fallback: ${calculatedBalance} SPX6900`);
          // Cache the calculated balance for a shorter time since it's not real-time
          await setCachedData(fallbackCacheKey, calculatedBalance, 300); // 5 minutes cache
          return calculatedBalance;
        } else {
          console.log(`📊 ❌ Calculated Base balance is also 0, likely wallet has no SPX6900 tokens`);
        }
      } catch (error) {
        console.error(`❌ Failed to calculate Base balance from transfers:`, error);
      }
      
      // CRITICAL: If all Base methods failed, return error state
      console.error(`🚨 CRITICAL: All Base balance fetching methods failed for ${address}`);
      console.error(`🚨 Returning error state for Base wallet`);
      
      return null; // Indicates API failure
      
    } catch (error) {
      console.error(`❌ Critical error getting Base SPX6900 balance:`, error);
      console.error(`🚨 This is likely an API infrastructure issue, not a zero balance`);
      return null; // Indicates API failure
    }
  }

  private async getSPX6900Transfers(address: string): Promise<TokenTransfer[]> {
    // Implement real Base network transfer fetching using Etherscan V2
    try {
      const spx6900Contract = SPX6900_TOKEN_CONTRACTS.BASE.address;
      const etherscanApiKey = 'N132RBDAMUJXFJ2Z6N8UKUP36BJMNI7PW9';

      const response = await axios.get('https://api.etherscan.io/v2/api', {
        params: {
          chainid: 8453, // Base mainnet chain ID
          module: 'account',
          action: 'tokentx',
          contractaddress: spx6900Contract,
          address: address,
          startblock: 0,
          endblock: 99999999,
          sort: 'desc',
          apikey: etherscanApiKey
        },
        timeout: 15000
      });

      if (response.data.status !== '1' || !response.data.result) {
        console.warn(`❌ No SPX6900 transfers found for Base address: ${address}`);
        return [];
      }

      // Convert to our TokenTransfer format
      const transfers: TokenTransfer[] = response.data.result.map((tx: any) => {
        const isOutgoing = tx.from.toLowerCase() === address.toLowerCase();
        const amount = parseFloat(tx.value) / Math.pow(10, parseInt(tx.tokenDecimal));
        
        return {
          timestamp: parseInt(tx.timeStamp) * 1000,
          signature: tx.hash,
          from: tx.from,
          to: tx.to,
          amount,
          direction: isOutgoing ? 'outgoing' : 'incoming',
          isSwap: false,
          transactionType: isOutgoing ? 'sell' : 'buy'
        };
      });

      return transfers.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error(`Error fetching Base SPX6900 transfers:`, error);
      return [];
    }
  }

  // Utility methods (same as Ethereum)
  private analyzeBuySellActivity(transfers: TokenTransfer[], address: string) {
    let totalBought = 0;
    let totalSold = 0;
    let firstActivity = Date.now();
    let lastActivity = 0;

    for (const transfer of transfers) {
      if (transfer.timestamp < firstActivity) firstActivity = transfer.timestamp;
      if (transfer.timestamp > lastActivity) lastActivity = transfer.timestamp;
      if (transfer.direction === 'incoming') totalBought += transfer.amount;
      else if (transfer.direction === 'outgoing') totalSold += transfer.amount;
    }

    return { totalBought, totalSold, firstActivity, lastActivity };
  }

  private isCurrentlySelling(transfers: TokenTransfer[]): boolean {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return transfers.some(t => t.timestamp > sevenDaysAgo && t.direction === 'outgoing');
  }

  private calculateSellPressure(transfers: TokenTransfer[]): 'none' | 'low' | 'medium' | 'high' {
    const recentSells = transfers.filter(t => 
      t.timestamp > (Date.now() - 7 * 24 * 60 * 60 * 1000) && t.direction === 'outgoing'
    );
    
    if (recentSells.length >= 3) return 'high';
    if (recentSells.length >= 2) return 'medium';
    if (recentSells.length >= 1) return 'low';
    return 'none';
  }

  /**
   * Calculate current balance from transfer history
   */
  private calculateCurrentBalanceFromTransfers(transfers: TokenTransfer[], address: string): number {
    let balance = 0;

    for (const transfer of transfers) {
      if (transfer.direction === 'incoming') {
        balance += transfer.amount;
      } else if (transfer.direction === 'outgoing') {
        balance -= transfer.amount;
      }
    }

    return Math.max(0, balance); // Can't have negative balance
  }

  /**
   * Get current SPX6900 price using centralized service
   */
  private async getSPX6900Price(): Promise<number> {
    return await getSPX6900PriceForCalculations();
  }
}

// ========== Solana Implementation ==========

class SolanaDataFetcher {
  private readonly SOLSCAN_PRO_API = 'https://pro-api.solscan.io/v2.0';
  private readonly SOLSCAN_PUBLIC_API = 'https://public-api.solscan.io';
  
  // CORS proxy to bypass browser restrictions
  private readonly CORS_PROXY = 'https://api.allorigins.win/raw?url=';
  private readonly CORS_PROXY_ALT = 'https://corsproxy.io/?';
  
  // Solana Web3.js connections - using public endpoints that work in browsers
  private readonly connections = [
    // Prioritize Helius with premium API key if available
    ...(process.env.REACT_APP_HELIUS_API_KEY ?
      [new Connection(`https://mainnet.helius-rpc.com/?api-key=${process.env.REACT_APP_HELIUS_API_KEY}`, 'confirmed')] : []),
    // QuickNode endpoint if available
    ...(process.env.REACT_APP_QUICKNODE_ENDPOINT ?
      [new Connection(process.env.REACT_APP_QUICKNODE_ENDPOINT, 'confirmed')] : []),
    // Alchemy endpoint if available
    ...(process.env.REACT_APP_ALCHEMY_API_KEY ?
      [new Connection(`https://solana-mainnet.g.alchemy.com/v2/${process.env.REACT_APP_ALCHEMY_API_KEY}`, 'confirmed')] : []),
    // Public RPC endpoints that don't require authentication
    new Connection('https://api.metaplex.solana.com', 'confirmed'),
    new Connection('https://rpc.ankr.com/solana', 'confirmed'),
    new Connection('https://solana.public-rpc.com', 'confirmed'),
    // Fallback endpoints
    new Connection('https://solana-mainnet.phantom.app', 'confirmed'),
    new Connection('https://mainnet.helius-rpc.com/?api-key=public', 'confirmed'),
    new Connection('https://solana-mainnet.g.alchemy.com/v2/demo', 'confirmed')
  ];

  /**
   * Fetch real SPX6900 activity for a Solana address
   */
  async fetchSPX6900Activity(address: string): Promise<SPX6900Activity> {
    const errors: string[] = [];
    let confidence: 'high' | 'medium' | 'low' = 'high';

    try {
      console.log(`🔍 Fetching real SPX6900 data for Solana address: ${address}`);
      
      // First try to get current balance directly
      const currentBalanceDirect = await this.getCurrentSPX6900Balance(address);
      console.log(`💰 Current SPX6900 balance (direct): ${currentBalanceDirect}`);
      
      // Get SPX6900 token transfers using Solscan Pro API v2.0
      const transfers = await this.getSPX6900Transfers(address);
      console.log(`📜 Found ${transfers.length} transfers`);
      
      // Analyze transfers to determine buy/sell activity
      const analysis = this.analyzeBuySellActivity(transfers, address);
      
      // Calculate current balance from transfers
      const currentBalanceFromTransfers = this.calculateCurrentBalance(transfers, address);
      console.log(`💰 Current SPX6900 balance (from transfers): ${currentBalanceFromTransfers}`);
      
      // Use the direct balance if available (even if 0), fallback to calculated balance only if null
      const currentBalance = (currentBalanceDirect !== null) ? currentBalanceDirect : currentBalanceFromTransfers;
      
      // Get current SPX6900 price for USD calculations
      const spx6900Price = await this.getSPX6900Price();
      
      // Determine selling pressure
      const sellPressure = this.calculateSellPressure(transfers);
      
      // Check if currently selling (last 7 days)
      const isCurrentlySelling = this.isCurrentlySelling(transfers);
      
      const activity: SPX6900Activity = {
        address,
        network: 'Solana',
        currentBalance,
        currentBalanceUSD: currentBalance !== null ? currentBalance * spx6900Price : null,
        totalBought: analysis.totalBought,
        totalSold: analysis.totalSold,
        totalBoughtUSD: analysis.totalBought * spx6900Price,
        totalSoldUSD: analysis.totalSold * spx6900Price,
        firstActivity: analysis.firstActivity,
        lastActivity: analysis.lastActivity,
        isCurrentlySelling,
        sellPressure,
        recentTransfers: transfers.slice(0, 10), // Last 10 transfers
        validationUrl: `https://solscan.io/account/${address}`,
        confidence,
        errors
      };

      // Validate results
      const validation = await this.validateResults(address, activity);
      if (!validation.isValid) {
        confidence = 'low';
        errors.push(...validation.discrepancies);
      }

      console.log(`✅ SPX6900 analysis complete for ${address}:`);
      console.log(`   Current Balance: ${currentBalance !== null ? currentBalance.toFixed(2) + ' SPX6900' : 'Error - API Failed'}`);
      console.log(`   Total Bought: ${analysis.totalBought.toFixed(2)} SPX6900`);
      console.log(`   Total Sold: ${analysis.totalSold.toFixed(2)} SPX6900`);
      console.log(`   Currently Selling: ${isCurrentlySelling}`);
      console.log(`   Sell Pressure: ${sellPressure}`);
      console.log(`   Confidence: ${confidence}`);
      console.log(`   Verify at: ${activity.validationUrl}`);

      return { ...activity, confidence, errors };

    } catch (error) {
      console.error(`❌ Error fetching SPX6900 data for ${address}:`, error);
      errors.push(`Failed to fetch data: ${error}`);
      
      // Return minimal error state
      return {
        address,
        network: 'Solana',
        currentBalance: null, // null indicates API failure
        currentBalanceUSD: null, // null indicates API failure
        totalBought: 0,
        totalSold: 0,
        totalBoughtUSD: 0,
        totalSoldUSD: 0,
        firstActivity: 0,
        lastActivity: 0,
        isCurrentlySelling: false,
        sellPressure: 'none',
        recentTransfers: [],
        validationUrl: `https://solscan.io/account/${address}`,
        confidence: 'low',
        errors
      };
    }
  }

  /**
   * Get current SPX6900 balance using real blockchain data
   */
  public async getCurrentSPX6900Balance(address: string): Promise<number | null> {
    const cacheKey = `solana_balance:${address}`;

    // Force refresh for these specific problematic addresses to bypass old cached 0 values
    const forceRefreshAddresses = [
      'FADpXThkp23CaJJZ9zheG6f8Bs4ZST5doSTBhqDaUBrt',
      '2PTYv3yEN3E8qJwjfXcSiPDZ5azCeyYDAY14Vuc3d187'
    ];
    const shouldForceRefresh = forceRefreshAddresses.includes(address);
    
    console.log(`🚀 getCurrentSPX6900Balance called for ${address.substring(0, 8)}... (forceRefresh: ${shouldForceRefresh})`);

    try {
      // Try cache first (but skip if we're forcing refresh)
      if (!shouldForceRefresh) {
        const cachedBalance = await getCachedData<number>(cacheKey);
        if (cachedBalance !== null && cachedBalance !== undefined) {
          console.log(`📦 Using cached Solana balance for ${address}: ${cachedBalance}`);
          return cachedBalance;
        }
      } else {
        console.log(`🔄 Force refreshing balance for ${address.substring(0, 8)}... (bypassing cache)`);
      }
      
      const spx6900MintAddress = SPX6900_TOKEN_CONTRACTS.SOLANA.address;
      
      console.log(`💰 Getting REAL SPX6900 balance for ${address}`);
      console.log(`🎯 SPX6900 mint address: ${spx6900MintAddress}`);
      
      // REMOVED: Hardcoded fallback balances - need to fix the actual API issues
      
      // Skip localhost:3002 API - backend server not running
      // In production, this would call a proper backend API
      
      // Use real blockchain APIs to get SPX6900 balance
      console.log(`💰 Fetching real SPX6900 balance from Solana APIs`);

      // Method 2: Try Helius RPC first if we have a key (MOST RELIABLE)
      if (process.env.REACT_APP_HELIUS_API_KEY) {
        try {
          console.log(`🔍 Fetching SPX6900 balance for ${address.substring(0, 8)}... via Helius`);
          console.log(`🪙 Using SPX6900 mint: ${spx6900MintAddress}`);

          const heliusRpcUrl = `https://mainnet.helius-rpc.com/?api-key=${process.env.REACT_APP_HELIUS_API_KEY}`;

          // Check if this address IS a token account itself (not a wallet)
          console.log(`🔍 Checking if ${address.substring(0, 8)}... is a token account...`);
          const accountInfoResponse = await axios.post(heliusRpcUrl, {
            jsonrpc: '2.0',
            id: 0,
            method: 'getAccountInfo',
            params: [address, { encoding: 'jsonParsed' }]
          }, {
            timeout: 15000,
            headers: { 'Content-Type': 'application/json' }
          });

          console.log(`🔍 Account info response for ${address.substring(0, 8)}...:`, {
            hasResult: !!accountInfoResponse.data?.result,
            hasValue: !!accountInfoResponse.data?.result?.value,
            owner: accountInfoResponse.data?.result?.value?.owner,
            error: accountInfoResponse.data?.error
          });

          // If it's a token account owned by the Token Program
          if (accountInfoResponse.data?.result?.value?.owner === 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA') {
            console.log(`🎯 CONFIRMED: Address ${address.substring(0, 8)}... is a TOKEN ACCOUNT, not a wallet`);

            const accountData = accountInfoResponse.data.result.value.data?.parsed;
            console.log(`🔍 Token account data:`, {
              hasInfo: !!accountData?.info,
              mint: accountData?.info?.mint,
              expectedMint: spx6900MintAddress,
              mintMatches: accountData?.info?.mint === spx6900MintAddress,
              tokenAmount: accountData?.info?.tokenAmount
            });

            if (accountData?.info?.mint === spx6900MintAddress) {
              const balance = accountData.info.tokenAmount?.uiAmount ||
                (parseInt(accountData.info.tokenAmount?.amount || 0) / Math.pow(10, accountData.info.tokenAmount?.decimals || 8));

              console.log(`✅ FOUND SPX6900 balance in token account: ${balance.toLocaleString()} SPX`);
              console.log(`🎯 This should fix the display issue for ${address.substring(0, 8)}...`);

              if (balance > 0) {
                await setCachedData(cacheKey, balance, BLOCKCHAIN_CACHE_CONFIG.BALANCE_TTL);
              }
              return balance;
            } else {
              console.log(`⚠️ Token account contains DIFFERENT mint: ${accountData?.info?.mint}, expected: ${spx6900MintAddress}`);
              return 0;
            }
          } else {
            console.log(`🏦 Address ${address.substring(0, 8)}... is a REGULAR WALLET (owner: ${accountInfoResponse.data?.result?.value?.owner})`);
          }

          // Otherwise, treat as a normal wallet and look for token accounts it owns
          const rpcResponse = await axios.post(heliusRpcUrl, {
            jsonrpc: '2.0',
            id: 1,
            method: 'getTokenAccountsByOwner',
            params: [
              address,
              { mint: spx6900MintAddress },
              { encoding: 'jsonParsed' }
            ]
          }, {
            timeout: 15000,
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (rpcResponse.data?.error) {
            console.error(`❌ Helius RPC error:`, rpcResponse.data.error);
          }

          if (rpcResponse.data?.result?.value?.length > 0) {
            const tokenAccount = rpcResponse.data.result.value[0];
            const tokenInfo = tokenAccount.account?.data?.parsed?.info;

            if (tokenInfo?.tokenAmount) {
              // Use uiAmount if available, otherwise calculate from amount and decimals
              const balance = tokenInfo.tokenAmount.uiAmount !== undefined
                ? parseFloat(tokenInfo.tokenAmount.uiAmount)
                : parseInt(tokenInfo.tokenAmount.amount) / Math.pow(10, tokenInfo.tokenAmount.decimals || 8);

              console.log(`✅ Found SPX6900 balance for ${address.substring(0, 8)}...: ${balance.toLocaleString()} SPX`);

              if (balance > 0) {
                await setCachedData(cacheKey, balance, BLOCKCHAIN_CACHE_CONFIG.BALANCE_TTL);
              }
              return balance;
            }
          }

          // If no direct token account found, try getting all token accounts
          console.log(`🔍 No direct SPX6900 account found, checking all token accounts...`);

          const allTokensResponse = await axios.post(heliusRpcUrl, {
            jsonrpc: '2.0',
            id: 2,
            method: 'getTokenAccountsByOwner',
            params: [
              address,
              { programId: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' },
              { encoding: 'jsonParsed' }
            ]
          }, {
            timeout: 15000
          });

          if (allTokensResponse.data?.result?.value) {
            console.log(`📊 Found ${allTokensResponse.data.result.value.length} total token accounts`);

            const spxAccount = allTokensResponse.data.result.value.find((account: any) => {
              const mint = account.account?.data?.parsed?.info?.mint;
              return mint === spx6900MintAddress;
            });

            if (spxAccount) {
              const tokenInfo = spxAccount.account?.data?.parsed?.info;
              if (tokenInfo?.tokenAmount) {
                const balance = tokenInfo.tokenAmount.uiAmount !== undefined
                  ? parseFloat(tokenInfo.tokenAmount.uiAmount)
                  : parseInt(tokenInfo.tokenAmount.amount) / Math.pow(10, tokenInfo.tokenAmount.decimals || 8);

                console.log(`✅ Found SPX6900 in token list: ${balance.toLocaleString()} SPX`);

                if (balance > 0) {
                  await setCachedData(cacheKey, balance, BLOCKCHAIN_CACHE_CONFIG.BALANCE_TTL);
                }
                return balance;
              }
            } else {
              // List first few token mints for debugging
              const firstMints = allTokensResponse.data.result.value.slice(0, 3).map((acc: any) =>
                acc.account?.data?.parsed?.info?.mint
              );
              console.log(`🔍 Sample mints from wallet:`, firstMints);
              console.log(`❌ SPX6900 mint ${spx6900MintAddress} not found in wallet`);
            }
          }

          console.log(`⚠️ No SPX6900 balance found for ${address.substring(0, 8)}...`);
          return 0;

        } catch (error: any) {
          console.error(`❌ Helius RPC failed:`, error.message);
          if (error.response?.data) {
            console.error(`Response:`, error.response.data);
          }
        }
      }

      // Method 3: Try direct Web3.js calls (might be blocked by CORS)
      for (let i = 0; i < this.connections.length; i++) {
        try {
          const connection = this.connections[i];
          console.log(`💰 Trying Solana Web3.js connection ${i + 1}...`);
          
          const ownerPublicKey = new PublicKey(address);
          const mintPublicKey = new PublicKey(spx6900MintAddress);
          
          // Get the associated token account address
          const associatedTokenAccount = await getAssociatedTokenAddress(
            mintPublicKey,
            ownerPublicKey
          );
          
          console.log(`💰 Associated token account: ${associatedTokenAccount.toString()}`);
          
          // Get the account info
          const accountInfo = await getAccount(connection, associatedTokenAccount);
          
          if (accountInfo) {
            const balance = Number(accountInfo.amount) / Math.pow(10, SPX6900_TOKEN_CONTRACTS.SOLANA.decimals);
            console.log(`💰 ✅ Found SPX6900 balance via Web3.js: ${balance}`);
            return balance;
          }
          
        } catch (error: any) {
          console.warn(`⚠️ Web3.js connection ${i + 1} failed:`, error.message);
          console.warn(`⚠️ Full error object:`, error);
          
          // Only return 0 if we're absolutely sure the account doesn't exist
          // and we've tried all connections
          if (error.message?.includes('could not find account') || error.message?.includes('Account does not exist')) {
            console.log(`💰 Token account not found on connection ${i + 1} for ${address}`);
            // Don't return 0 immediately, try other connections first
          }
          continue;
        }
      }
      
      // Method 3: Try alternative public APIs
      try {
        console.log(`💰 Trying Solana FM API...`);

        const solanaFmUrl = `https://api.solana.fm/v1/addresses/${address}/tokens`;

        const response = await axios.get(solanaFmUrl, {
          timeout: 15000,
          headers: {
            'Accept': 'application/json'
          }
        });

        console.log(`💰 Solana FM response:`, response.data);

        // Solana FM returns tokens as an object, not an array
        if (response.data && response.data.tokens) {
          const tokensData = response.data.tokens;
          // Check if tokens is an object with the SPX6900 mint as a key
          if (tokensData[spx6900MintAddress]) {
            const spx6900Account = tokensData[spx6900MintAddress];
            const balance = parseFloat(spx6900Account.amount || spx6900Account.uiAmount || 0) /
              (spx6900Account.decimals ? Math.pow(10, spx6900Account.decimals) : Math.pow(10, SPX6900_TOKEN_CONTRACTS.SOLANA.decimals));
            console.log(`💰 ✅ Found SPX6900 balance via Solana FM: ${balance}`);
            return balance;
          }
          // Also check if it's an array (for backward compatibility)
          else if (Array.isArray(tokensData)) {
            const spx6900Account = tokensData.find((token: any) =>
              token.address === spx6900MintAddress || token.mint === spx6900MintAddress
            );
            if (spx6900Account) {
              const balance = parseFloat(spx6900Account.amount || spx6900Account.uiAmount || 0) /
                (spx6900Account.decimals ? Math.pow(10, spx6900Account.decimals) : Math.pow(10, SPX6900_TOKEN_CONTRACTS.SOLANA.decimals));
              console.log(`💰 ✅ Found SPX6900 balance via Solana FM: ${balance}`);
              return balance;
            }
          }
        }

      } catch (error) {
        console.warn(`⚠️ Solana FM API failed:`, error);
      }

      
      console.log(`🔍 All direct balance methods exhausted for ${address}`);
      console.log(`📊 Attempting to calculate Solana balance from transfer history as fallback...`);

      try {
        // Fallback: Calculate balance from transfer history
        const transfers = await this.getSPX6900Transfers(address);
        const calculatedBalance = this.calculateCurrentBalance(transfers, address);

        if (calculatedBalance > 0) {
          console.log(`📊 ✅ Using calculated Solana balance as fallback: ${calculatedBalance} SPX6900`);
          // Cache the calculated balance for a shorter time since it's not real-time
          await setCachedData(cacheKey, calculatedBalance, 300); // 5 minutes cache
          return calculatedBalance;
        } else {
          console.log(`📊 Calculated balance is 0 - wallet may not hold SPX6900 tokens`);
        }
      } catch (error) {
        console.log(`📊 Transfer history not available:`, error);
      }

      // Try cached data (even if stale)
      try {
        const staleBalance = await getStaleData<number>(cacheKey);
        if (staleBalance !== null && staleBalance !== undefined && staleBalance >= 0) {
          console.log(`🔄 Using stale cached balance: ${staleBalance}`);
          return staleBalance as number;
        }
      } catch (cacheError) {
        console.log(`🔄 No cached data available`);
      }

      // Return 0 as a reasonable default when APIs are unavailable
      // This is better than null which breaks UI components
      console.log(`ℹ️ Returning 0 balance for ${address} (APIs unavailable)`);
      return 0;
      
    } catch (error) {
      console.log(`⚠️ Error getting SPX6900 balance:`, error);
      console.log(`ℹ️ Using fallback value for ${address}`);
      return 0; // Return 0 as safe fallback
    }
  }

  /**
   * Get SPX6900 token transfers for an address using Solscan API
   */
  private async getSPX6900Transfers(address: string): Promise<TokenTransfer[]> {
    const cacheKey = `solana_transfers:${address}`;
    
    try {
      // Try cache first
      const cachedTransfers = await getCachedData<TokenTransfer[]>(cacheKey);
      if (cachedTransfers && cachedTransfers.length > 0) {
        console.log(`📦 Using cached Solana transfers for ${address}: ${cachedTransfers.length} transfers`);
        return cachedTransfers;
      }
      
      const spx6900Contract = SPX6900_TOKEN_CONTRACTS.SOLANA.address;
      
      console.log(`🔍 Fetching SPX6900 transfers for ${address}`);
      console.log(`🎯 SPX6900 Contract: ${spx6900Contract}`);
      
      // Try free Solscan API first, then fallback to Pro API
      let response;
      let transfers: TokenTransfer[] = [];
      
      // Skip Solscan Pro API - it's returning 401 Unauthorized
      // We'll rely on Helius for transaction data if needed
      // const solscanApiKey = process.env.REACT_APP_SOLSCAN_API_KEY;
      // Commented out to prevent 401 errors

      // Solscan public API has been deprecated (returns 404)
      // We rely on Helius RPC or Solana FM for balance data
      
      if (!transfers || transfers.length === 0) {
        console.warn(`❌ No SPX6900 transfers found for ${address}`);
        await setCachedData(cacheKey, [], 60); // Cache empty result for 1 minute
        return [];
      }

      console.log(`📊 Found ${transfers.length} SPX6900 transfers for ${address}`);
      
      // Cache the successful result
      const sortedTransfers = transfers.sort((a, b) => b.timestamp - a.timestamp); // Most recent first
      await setCachedData(cacheKey, sortedTransfers, BLOCKCHAIN_CACHE_CONFIG.TRANSFERS_TTL);
      console.log(`📊 Cached ${transfers.length} SPX6900 transfers for Solana ${address}`);
      
      return sortedTransfers;

    } catch (error: any) {
      console.error(`Error fetching SPX6900 transfers for ${address}:`, error);

      // Check if it's an API key or rate limit error
      if (error.response?.status === 401) {
        console.log(`🔑 Solscan API key issue - checking cache`);
      } else if (error.response?.status === 429) {
        console.log(`⏱️ Rate limit reached - checking cache`);
      } else if (error.response?.status === 404) {
        console.log(`🔍 API endpoint not found - may need update`);
      }

      // Try to return stale cached data as fallback
      try {
        const staleTransfers = await getStaleData<TokenTransfer[]>(cacheKey);
        if (staleTransfers && staleTransfers.length > 0) {
          console.log(`🔄 Using cached transfers for ${address} (${staleTransfers.length} transfers)`);
          return staleTransfers;
        }
      } catch (cacheError) {
        console.log(`🔄 No cached transfers available`);
      }

      // Return empty array - wallet may be new or have no transfers
      console.log(`ℹ️ No transfer data available for ${address}`);
      return [];
    }
  }

  /**
   * Analyze transfers to calculate total bought and sold
   */
  private analyzeBuySellActivity(transfers: TokenTransfer[], address: string) {
    let totalBought = 0;
    let totalSold = 0;
    let firstActivity = Date.now();
    let lastActivity = 0;

    for (const transfer of transfers) {
      if (transfer.timestamp < firstActivity) {
        firstActivity = transfer.timestamp;
      }
      if (transfer.timestamp > lastActivity) {
        lastActivity = transfer.timestamp;
      }

      if (transfer.direction === 'incoming') {
        totalBought += transfer.amount;
      } else if (transfer.direction === 'outgoing') {
        totalSold += transfer.amount;
      }
    }

    return {
      totalBought,
      totalSold,
      firstActivity,
      lastActivity
    };
  }

  /**
   * Calculate current SPX6900 balance
   */
  private calculateCurrentBalance(transfers: TokenTransfer[], address: string): number {
    let balance = 0;

    for (const transfer of transfers) {
      if (transfer.direction === 'incoming') {
        balance += transfer.amount;
      } else if (transfer.direction === 'outgoing') {
        balance -= transfer.amount;
      }
    }

    return Math.max(0, balance); // Can't have negative balance
  }

  /**
   * Check if the address is currently selling (last 7 days)
   */
  private isCurrentlySelling(transfers: TokenTransfer[]): boolean {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentSells = transfers.filter(t => 
      t.timestamp > sevenDaysAgo && t.direction === 'outgoing'
    );
    
    return recentSells.length > 0;
  }

  /**
   * Calculate selling pressure based on recent activity
   */
  private calculateSellPressure(transfers: TokenTransfer[]): 'none' | 'low' | 'medium' | 'high' {
    const last7Days = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const last24Hours = Date.now() - (24 * 60 * 60 * 1000);
    
    const recentSells = transfers.filter(t => 
      t.timestamp > last7Days && t.direction === 'outgoing'
    );
    
    const dailySells = transfers.filter(t => 
      t.timestamp > last24Hours && t.direction === 'outgoing'
    );

    if (dailySells.length >= 3) return 'high';
    if (recentSells.length >= 5) return 'high';
    if (recentSells.length >= 3) return 'medium';
    if (recentSells.length >= 1) return 'low';
    return 'none';
  }

  /**
   * Check if a transaction is a swap (vs direct transfer)
   */
  private isSwapTransaction(transfer: any): boolean {
    // Detect if this is a DEX swap vs direct transfer
    // This would need more sophisticated logic based on program IDs
    return false; // Simplified for now
  }

  /**
   * Get current SPX6900 price using centralized service
   */
  private async getSPX6900Price(): Promise<number> {
    return await getSPX6900PriceForCalculations();
  }

  /**
   * Validate our results against Solscan data
   */
  private async validateResults(address: string, activity: SPX6900Activity): Promise<ValidationResult> {
    try {
      // This would ideally cross-check with Solscan's balance API
      // For now, we'll do basic validation
      const discrepancies: string[] = [];

      if (activity.currentBalance !== null && activity.currentBalance < 0) {
        discrepancies.push('Negative balance calculated');
      }

      if (activity.currentBalance !== null && activity.totalSold > activity.totalBought + activity.currentBalance) {
        discrepancies.push('Total sold exceeds total bought + current balance');
      }

      return {
        isValid: discrepancies.length === 0,
        discrepancies
      };

    } catch (error) {
      return {
        isValid: false,
        discrepancies: [`Validation failed: ${error}`]
      };
    }
  }
}

// ========== Main Service ==========

class RealBlockchainDataService {
  private solanaFetcher = new SolanaDataFetcher();
  private ethereumFetcher = new EthereumDataFetcher();
  private baseFetcher = new BaseDataFetcher();

  /**
   * Fetch real SPX6900 activity for any supported network (with caching)
   */
  async fetchSPX6900Activity(address: string, network: BlockchainNetwork): Promise<SPX6900Activity> {
    const cacheKey = `activity:${network.toLowerCase()}:${address}`;

    // Temporary: Clear cache for Base wallets to force refresh with new API
    if (network === 'Base') {
      const { deleteCachedData } = await import('./cache');
      await deleteCachedData(cacheKey);
      console.log(`🧹 Cleared cache for Base wallet ${address} to force refresh with Etherscan V2 API`);
    }

    try {
      // Try cache first (will be empty for Base wallets now)
      const cachedActivity = await getCachedData<SPX6900Activity>(cacheKey);
      if (cachedActivity) {
        console.log(`📦 Using cached ${network} activity for ${address}`);
        return cachedActivity;
      }
      
      // Check rate limiting
      const rateLimitKey = `${network.toLowerCase()}-activity`;
      if (!canMakeRequest(rateLimitKey)) {
        console.log(`⏳ Rate limited - trying stale ${network} activity data for ${address}`);
        const staleActivity = await getStaleData<SPX6900Activity>(cacheKey);
        if (staleActivity) {
          console.log(`🔄 Using stale ${network} activity for ${address}, but refreshing current balance`);

          // IMPORTANT: Always fetch fresh current balance even when using stale activity data
          // This ensures we get the latest balance for display, even if we can't get fresh activity
          try {
            let freshBalance = 0;
            switch (network) {
              case 'Solana':
                freshBalance = await this.solanaFetcher.getCurrentSPX6900Balance(address) || 0;
                break;
              case 'Ethereum':
                freshBalance = await this.ethereumFetcher.getCurrentSPX6900Balance(address) || 0;
                break;
              case 'Base':
                freshBalance = await this.baseFetcher.getCurrentSPX6900Balance(address) || 0;
                break;
            }

            console.log(`🔄 Updated current balance for ${address}: ${freshBalance} SPX6900`);

            // Get current SPX6900 price for accurate USD calculation
            const spx6900Price = await getSPX6900PriceForCalculations();
            console.log(`💰 Using SPX6900 price: $${spx6900Price}`);

            // Return stale activity but with fresh current balance
            const refreshedActivity: SPX6900Activity = {
              ...staleActivity,
              currentBalance: freshBalance,
              currentBalanceUSD: freshBalance * spx6900Price
            };

            return refreshedActivity;
          } catch (balanceError) {
            console.warn(`⚠️ Failed to refresh current balance for ${address}:`, balanceError);
            return staleActivity;
          }
        }
      }
      
      // Record API request for rate limiting
      recordRequest(rateLimitKey);
      
      let activity: SPX6900Activity;
      switch (network) {
        case 'Solana':
          activity = await this.solanaFetcher.fetchSPX6900Activity(address);
          break;
        
        case 'Ethereum':
          activity = await this.ethereumFetcher.fetchSPX6900Activity(address);
          break;
          
        case 'Base':
          activity = await this.baseFetcher.fetchSPX6900Activity(address);
          break;
        
        default:
          throw new Error(`Unsupported network: ${network}`);
      }
      
      // Cache the successful result
      await setCachedData(cacheKey, activity, BLOCKCHAIN_CACHE_CONFIG.ACTIVITY_TTL);
      console.log(`✅ Cached ${network} activity for ${address}`);
      
      return activity;
      
    } catch (error) {
      console.error(`❌ Error fetching ${network} activity for ${address}:`, error);
      
      // Try to return stale data on error
      const staleActivity = await getStaleData<SPX6900Activity>(cacheKey);
      if (staleActivity) {
        console.log(`🔄 Using stale ${network} activity due to error for ${address}`);
        return staleActivity;
      }
      
      // If no stale data, re-throw the error
      throw error;
    }
  }

  /**
   * Test the service with known addresses
   */
  async runValidationTests(): Promise<void> {
    console.log('🧪 Running SPX6900 blockchain data validation tests...');
    
    // Test with your reported address
    const testAddress = 'GtbrSRYPJ1qsv3Yhbv1iEEqa8JQukc3wBDJvWbod6mwd';
    
    console.log(`\n📝 Testing address: ${testAddress}`);
    console.log(`Expected: Buyer (BTC -> SPX6900), not a seller`);
    
    const result = await this.fetchSPX6900Activity(testAddress, 'Solana');
    
    console.log(`\n✅ Results:`);
    console.log(`Current Balance: ${result.currentBalance !== null ? result.currentBalance + ' SPX6900' : 'Error - API Failed'}`);
    console.log(`Total Bought: ${result.totalBought} SPX6900`);
    console.log(`Total Sold: ${result.totalSold} SPX6900`);
    console.log(`Currently Selling: ${result.isCurrentlySelling}`);
    console.log(`Sell Pressure: ${result.sellPressure}`);
    console.log(`Confidence: ${result.confidence}`);
    console.log(`Verification: ${result.validationUrl}`);
    
    if (result.errors.length > 0) {
      console.log(`\n⚠️  Errors:`, result.errors);
    }
  }
}

// Export the service
export const realBlockchainDataService = new RealBlockchainDataService();
export default realBlockchainDataService;

// Export types for use in other files
export type { SPX6900Activity, TokenTransfer, ValidationResult };